/**
 * useAvailabilityForm Composable (ViewModel)
 *
 * Main ViewModel for the AvailabilityConfigurationModal.
 * Orchestrates form state, validation, and existing composables (usePricing, useOverbooking).
 */
import { ref, reactive, computed, watch, toRaw } from "vue";
import { translateApp } from "@/i18n";
import { usePricing } from "./usePricing";
import { useOverbooking } from "./useOverbooking";
import { systemConfigService } from "@domains/shared/services/system-config.service";
import { pricingService, DEFAULT_TAX_RATE } from "../services/pricing.service";
import { serviceChargeRepository } from "@domains/charges/repositories/service-charge.repository";
import { availabilityTransformer } from "../utils/availability-transformer";
import { timeValidator } from "../utils/time-validator";
import { DEFAULT_FORM_STATE, } from "../models/availability-form.types";
// =============================================================================
// COMPOSABLE
// =============================================================================
export function useAvailabilityForm(options) {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    const formState = reactive({ ...DEFAULT_FORM_STATE });
    const selectedOption = ref(defaultSelectedOption(options.modePolicy));
    const showOverbookingConfig = ref(false);
    const editingId = ref(null);
    const initialStateSnapshot = ref(null);
    const serviceCharges = ref([]);
    const runtimePricingConfig = ref(systemConfigService.getCached());
    const isRuntimePricingConfigLoading = ref(true);
    systemConfigService
        .fetch()
        .then((config) => {
        runtimePricingConfig.value = config;
    })
        .finally(() => {
        isRuntimePricingConfigLoading.value = false;
    });
    watch(() => options.service?.id, async (serviceId) => {
        if (!serviceId) {
            serviceCharges.value = [];
            return;
        }
        try {
            const response = await serviceChargeRepository.getForService(serviceId);
            serviceCharges.value = [
                ...response.templates.map((template) => ({
                    name: template.chargeTemplate.name,
                    type: template.chargeTemplate.type,
                    amount: template.chargeTemplate.amount,
                    compounds_percentage: template.chargeTemplate.compounds_percentage,
                })),
                ...response.custom.map((charge) => ({
                    name: charge.name,
                    type: charge.type,
                    amount: charge.amount,
                    compounds_percentage: true,
                })),
            ];
        }
        catch {
            serviceCharges.value = [];
        }
    }, { immediate: true });
    // ---------------------------------------------------------------------------
    // COMPOSED: PRICING
    // ---------------------------------------------------------------------------
    const pricing = usePricing({
        initialNetPrice: formState.netPrice,
        initialCommissionAmount: formState.commissionAmount,
        initialCommissionType: formState.commissionType,
        initialPriceType: formState.priceType,
        taxRate: formState.taxRate,
    });
    // Sync form state → pricing composable
    watch(() => formState.netPrice, (val) => pricing.setNetPrice(val), { immediate: true });
    watch(() => formState.commissionAmount, (val) => pricing.setCommissionAmount(val), { immediate: true });
    watch(() => formState.commissionType, (val) => pricing.setCommissionType(val), { immediate: true });
    watch(() => formState.priceType, (val) => pricing.setPriceType(val), { immediate: true });
    // ---------------------------------------------------------------------------
    // COMPOSED: OVERBOOKING
    // ---------------------------------------------------------------------------
    const overbooking = useOverbooking({
        initialStandardCapacity: formState.standardMaxParticipants,
        initialOvercapacity: formState.overcapacityMaxParticipants,
        initialOverflowPrice: formState.overflowPrice,
        priceType: formState.priceType,
        netPrice: formState.netPrice,
    });
    // Sync form state → overbooking composable
    watch(() => formState.standardMaxParticipants, (val) => overbooking.setStandardCapacity(val), { immediate: true });
    watch(() => formState.overcapacityMaxParticipants, (val) => overbooking.setOvercapacity(val), { immediate: true });
    watch(() => formState.overflowPrice, (val) => overbooking.setOverflowPrice(val), { immediate: true });
    watch(() => formState.priceType, (newVal, oldVal) => {
        overbooking.setPriceType(newVal);
        // Only reset overbooking when SWITCHING to HOUR (not on initial load)
        if (newVal === "HOUR" && oldVal !== undefined && oldVal !== "HOUR") {
            showOverbookingConfig.value = false;
            formState.overcapacityMaxParticipants = null;
            formState.overflowPrice = null;
        }
    });
    // Handle overbooking toggle
    watch(showOverbookingConfig, (enabled) => {
        if (enabled) {
            // Only calculate defaults if values aren't already set (from existing data)
            const hasExistingValues = formState.overcapacityMaxParticipants != null &&
                formState.overflowPrice != null;
            if (!hasExistingValues) {
                overbooking.enable();
                // Sync defaults back to form state
                formState.overcapacityMaxParticipants = overbooking.overcapacity.value;
                formState.overflowPrice = overbooking.overflowPrice.value;
            }
        }
        else {
            overbooking.disable();
            formState.overcapacityMaxParticipants = null;
            formState.overflowPrice = null;
        }
    });
    // ---------------------------------------------------------------------------
    // COMPUTED: MODE
    // ---------------------------------------------------------------------------
    const isEditMode = computed(() => options.editingAvailability != null);
    const modePolicy = computed(() => options.modePolicy ?? "all-and-range");
    const mode = computed(() => {
        const isEdit = isEditMode.value;
        const isRange = selectedOption.value === "range";
        if (isEdit) {
            return isRange ? "update-override" : "update-calendar";
        }
        return isRange ? "create-override" : "create-calendar";
    });
    const canCreateOverride = computed(() => {
        // Can create override if:
        // - In edit mode (editing existing availability/override)
        // - Service has an ID (calendar auto-created with service)
        // - Explicit calendarId provided
        return (isEditMode.value ||
            Boolean(options.service?.id) ||
            Boolean(options.calendarId) ||
            options.allowDraftOverrides === true);
    });
    const canSelectAll = computed(() => {
        return ["all-and-range", "all-locked", "read-only"].includes(modePolicy.value);
    });
    const canSelectRange = computed(() => {
        return (["all-and-range", "range-only", "range-locked", "read-only"].includes(modePolicy.value) && canCreateOverride.value);
    });
    const isModeLocked = computed(() => {
        return ["all-locked", "range-locked", "read-only"].includes(modePolicy.value);
    });
    // ---------------------------------------------------------------------------
    // COMPUTED: VALIDATION
    // ---------------------------------------------------------------------------
    const validationErrors = computed(() => {
        const errors = [];
        const currentMode = mode.value;
        // Time validation (all modes)
        if (!formState.startTime) {
            errors.push({
                field: "startTime",
                message: translateApp("calendar.validation.start_time_required"),
            });
        }
        if (!formState.endTime) {
            errors.push({
                field: "endTime",
                message: translateApp("calendar.validation.end_time_required"),
            });
        }
        if (formState.startTime && formState.endTime) {
            if (!timeValidator.isEndTimeAfterStart(formState.startTime, formState.endTime)) {
                errors.push({
                    field: "endTime",
                    message: translateApp("calendar.validation.end_after_start"),
                });
            }
        }
        // Day selection (calendar modes only)
        if (currentMode === "create-calendar" ||
            currentMode === "update-calendar") {
            if (formState.days.length === 0) {
                errors.push({
                    field: "days",
                    message: translateApp("calendar.validation.day_required"),
                });
            }
        }
        // Date range (override modes only)
        if (currentMode === "create-override" ||
            currentMode === "update-override") {
            if (!formState.dateRange.start) {
                errors.push({
                    field: "dateRange.start",
                    message: translateApp("calendar.validation.start_date_required"),
                });
            }
            if (!formState.dateRange.end) {
                errors.push({
                    field: "dateRange.end",
                    message: translateApp("calendar.validation.end_date_required"),
                });
            }
            if (formState.dateRange.start && formState.dateRange.end) {
                if (!timeValidator.isEndDateAfterOrEqual(formState.dateRange.start, formState.dateRange.end)) {
                    errors.push({
                        field: "dateRange.end",
                        message: translateApp("calendar.validation.end_date_after_start"),
                    });
                }
            }
        }
        // Overbooking validation
        if (showOverbookingConfig.value && !overbooking.isValid.value) {
            overbooking.errors.value.forEach((msg) => {
                errors.push({ field: "overbooking", message: msg });
            });
        }
        // Capacity validation
        if (formState.standardMaxParticipants < 1) {
            errors.push({
                field: "standardMaxParticipants",
                message: translateApp("calendar.validation.capacity_min"),
            });
        }
        // Net price validation
        if (formState.netPrice < 0) {
            errors.push({
                field: "netPrice",
                message: translateApp("calendar.validation.net_price_non_negative"),
            });
        }
        return errors;
    });
    const isValid = computed(() => validationErrors.value.length === 0);
    // ---------------------------------------------------------------------------
    // COMPUTED: DIRTY STATE
    // ---------------------------------------------------------------------------
    const isDirty = computed(() => {
        if (!initialStateSnapshot.value)
            return false;
        const currentSnapshot = JSON.stringify({
            formState: toRaw(formState),
            selectedOption: selectedOption.value,
            showOverbookingConfig: showOverbookingConfig.value,
        });
        return currentSnapshot !== initialStateSnapshot.value;
    });
    // ---------------------------------------------------------------------------
    // COMPUTED: PRICING OUTPUTS
    // ---------------------------------------------------------------------------
    const pricingBreakdown = computed(() => {
        return pricingService.getPriceBreakdown({
            netPrice: formState.netPrice,
            commissionAmount: formState.commissionAmount,
            commissionType: formState.commissionType,
            taxRate: formState.taxRate,
            extraCharges: serviceCharges.value,
            chargePercentageCompoundingEnabled: runtimePricingConfig.value?.chargePercentageCompoundingEnabled ?? false,
        });
    });
    const totalPrice = computed(() => pricingBreakdown.value.totalPrice);
    const providerBreakdown = computed(() => {
        const emantaRatePercent = runtimePricingConfig.value?.emantaCommissionRate ?? 0;
        const emantaRate = emantaRatePercent / 100;
        const servicePrice = pricingBreakdown.value.servicePrice;
        // ADR-0003: Emanta commission derives from the base net service price only,
        // never from additional charges.
        const netPrice = pricingBreakdown.value.netPrice;
        const taxRate = formState.taxRate ?? DEFAULT_TAX_RATE;
        const emantaCommission = pricingService.round(netPrice * emantaRate);
        const netIncome = pricingService.round(servicePrice - emantaCommission);
        const vatOnEmantaCommission = pricingService.round(emantaCommission * taxRate);
        const vatOnService = pricingService.round(netIncome * taxRate);
        const payout = pricingService.round(netIncome + vatOnService);
        return {
            servicePrice,
            emantaRate: emantaRatePercent,
            emantaCommission,
            netIncome,
            vatOnEmantaCommission,
            vatOnService,
            taxRate,
            payout,
            extraCharges: pricingBreakdown.value.extraCharges,
            extraChargeTotal: pricingBreakdown.value.extraChargeTotal,
        };
    });
    // ---------------------------------------------------------------------------
    // COMPUTED: OVERBOOKING OUTPUTS
    // ---------------------------------------------------------------------------
    const overbookingErrors = computed(() => overbooking.errors.value);
    const canEnableOverbooking = computed(() => overbooking.canEnable.value);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    function captureInitialState() {
        initialStateSnapshot.value = JSON.stringify({
            formState: toRaw(formState),
            selectedOption: selectedOption.value,
            showOverbookingConfig: showOverbookingConfig.value,
        });
    }
    function populateFromAvailability(availability) {
        const result = availabilityTransformer.fromApiToForm(availability);
        // Apply form state
        Object.assign(formState, { ...DEFAULT_FORM_STATE, ...result.formState });
        // Apply other state
        selectedOption.value = result.selectedOption;
        editingId.value = result.editingId;
        showOverbookingConfig.value = result.showOverbookingConfig;
        applyModePolicy();
        // Capture initial state after population
        captureInitialState();
    }
    function buildPayload() {
        if (!isValid.value)
            return null;
        const currentMode = mode.value;
        const input = {
            formState: toRaw(formState),
            serviceId: options.service?.id ?? null,
            editingId: editingId.value,
        };
        let payload;
        switch (currentMode) {
            case "create-calendar":
                payload = availabilityTransformer.toCreateCalendarPayload(input);
                break;
            case "create-override":
                payload = availabilityTransformer.toCreateOverridePayload(input);
                break;
            case "update-calendar":
                payload = availabilityTransformer.toUpdateCalendarPayload(input);
                break;
            case "update-override":
                payload = availabilityTransformer.toUpdateOverridePayload(input);
                break;
        }
        return { mode: currentMode, payload };
    }
    function reset() {
        Object.assign(formState, { ...DEFAULT_FORM_STATE });
        selectedOption.value = "all";
        showOverbookingConfig.value = false;
        editingId.value = null;
        initialStateSnapshot.value = null;
        pricing.reset();
        overbooking.reset();
        applyModePolicy();
    }
    function toggleDay(day) {
        const idx = formState.days.indexOf(day);
        if (idx > -1) {
            formState.days.splice(idx, 1);
        }
        else {
            formState.days.push(day);
        }
    }
    function setSelectedOption(option) {
        if (isModeLocked.value) {
            return;
        }
        if (option === "all" && canSelectAll.value) {
            selectedOption.value = "all";
        }
        if (option === "range" && canSelectRange.value) {
            selectedOption.value = "range";
        }
    }
    function applyModePolicy() {
        if (modePolicy.value === "range-only" ||
            modePolicy.value === "range-locked") {
            selectedOption.value = "range";
            return;
        }
        if (modePolicy.value === "all-locked") {
            selectedOption.value = "all";
            return;
        }
        if (selectedOption.value === "range" && !canSelectRange.value) {
            selectedOption.value = "all";
        }
        if (selectedOption.value === "all" && !canSelectAll.value) {
            selectedOption.value = "range";
        }
    }
    // ---------------------------------------------------------------------------
    // INITIALIZATION
    // ---------------------------------------------------------------------------
    // Populate if editing
    if (options.editingAvailability) {
        populateFromAvailability(options.editingAvailability);
    }
    else {
        applyModePolicy();
        captureInitialState();
    }
    // Clear date range when switching to 'all' mode
    watch(selectedOption, (newValue) => {
        applyModePolicy();
        if (newValue === "all") {
            formState.dateRange = { start: "", end: "" };
        }
    });
    watch(modePolicy, () => {
        applyModePolicy();
        captureInitialState();
    });
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        // State
        formState,
        selectedOption,
        showOverbookingConfig,
        editingId,
        // Computed
        mode,
        modePolicy,
        isEditMode,
        canCreateOverride,
        canSelectAll,
        canSelectRange,
        isModeLocked,
        isValid,
        validationErrors,
        isDirty,
        // Pricing
        totalPrice,
        pricingBreakdown,
        providerBreakdown,
        isRuntimePricingConfigLoading,
        // Overbooking
        overbookingErrors,
        canEnableOverbooking,
        // Actions
        populateFromAvailability,
        buildPayload,
        reset,
        toggleDay,
        setSelectedOption,
        captureInitialState,
    };
}
function defaultSelectedOption(modePolicy) {
    if (modePolicy === "range-only" || modePolicy === "range-locked") {
        return "range";
    }
    return "all";
}
